import { supabase } from '@/lib/supabase'
import { applyRecordStatusFilter } from '@/lib/api/helpers/apply-record-status-filter'
import { restoreRecord } from '@/lib/api/helpers/restore-record'
import type {
  ClientRow,
  ClientInsert,
  ClientUpdate,
  ClientTableRow,
  ClientFilters,
} from '@/types/clients'

/**
 * Fetch all clients for an account with optional filtering
 */
export async function fetchClients(
  organizationId: string,
  filters?: ClientFilters
): Promise<ClientTableRow[]> {
  let query = supabase
    .from('clients')
    .select(
      'id, client_number, client_type, first_name, last_name, company_name, email_primary, phone_primary, status, created_at, deleted_at'
    )
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  query = applyRecordStatusFilter(query, filters?.recordStatus ?? 'active')

  // Apply filters
  if (filters?.search) {
    query = query.or(
      `company_name.ilike.%${filters.search}%,first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,client_number.ilike.%${filters.search}%,email_primary.ilike.%${filters.search}%`
    )
  }

  if (filters?.type) {
    query = query.eq('client_type', filters.type)
  }

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }

  const { data, error } = await query

  if (error) throw error
  return (data as ClientTableRow[]) || []
}

/**
 * Fetch a single client by ID
 */
export async function fetchClient(id: string): Promise<ClientRow> {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as ClientRow
}

/**
 * Create a new client
 */
export async function createClient(data: ClientInsert): Promise<ClientRow> {
  const { data: created, error } = await supabase
    .from('clients')
    .insert({
      organization_id: data.organization_id,
      client_type: data.client_type,
      first_name: data.first_name ?? null,
      last_name: data.last_name ?? null,
      company_name: data.company_name ?? null,
      legal_entity_name: data.legal_entity_name ?? null,
      email_primary: data.email_primary ?? null,
      phone_primary: data.phone_primary ?? null,
      phone_secondary: data.phone_secondary ?? null,
      address_line1: data.address_line1 ?? null,
      address_line2: data.address_line2 ?? null,
      city: data.city ?? null,
      state_province: data.state_province ?? null,
      postal_code: data.postal_code ?? null,
      country: data.country ?? null,
      payment_terms: data.payment_terms ?? 'net_30',
      tax_exempt: data.tax_exempt ?? false,
      tax_id: data.tax_id ?? null,
      status: data.status ?? 'active',
      preferences: data.preferences ?? {},
      notes: data.notes ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .select()
    .single()

  if (error) throw error
  return created as ClientRow
}

/**
 * Update an existing client
 */
export async function updateClient(
  id: string,
  data: ClientUpdate
): Promise<ClientRow> {
  const updateData: Record<string, unknown> = {}

  if (data.client_type !== undefined) updateData.client_type = data.client_type
  if (data.first_name !== undefined) updateData.first_name = data.first_name
  if (data.last_name !== undefined) updateData.last_name = data.last_name
  if (data.company_name !== undefined) updateData.company_name = data.company_name
  if (data.legal_entity_name !== undefined)
    updateData.legal_entity_name = data.legal_entity_name
  if (data.email_primary !== undefined) updateData.email_primary = data.email_primary
  if (data.phone_primary !== undefined) updateData.phone_primary = data.phone_primary
  if (data.phone_secondary !== undefined)
    updateData.phone_secondary = data.phone_secondary
  if (data.address_line1 !== undefined) updateData.address_line1 = data.address_line1
  if (data.address_line2 !== undefined) updateData.address_line2 = data.address_line2
  if (data.city !== undefined) updateData.city = data.city
  if (data.state_province !== undefined)
    updateData.state_province = data.state_province
  if (data.postal_code !== undefined) updateData.postal_code = data.postal_code
  if (data.country !== undefined) updateData.country = data.country
  if (data.payment_terms !== undefined) updateData.payment_terms = data.payment_terms
  if (data.tax_exempt !== undefined) updateData.tax_exempt = data.tax_exempt
  if (data.tax_id !== undefined) updateData.tax_id = data.tax_id
  if (data.status !== undefined) updateData.status = data.status
  if (data.preferences !== undefined) updateData.preferences = data.preferences
  if (data.notes !== undefined) updateData.notes = data.notes

  const { data: updated, error } = await supabase
    .from('clients')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return updated as ClientRow
}

/**
 * Soft delete a client (sets deleted_at timestamp)
 */
export async function deleteClient(id: string): Promise<void> {
  const { error } = await supabase
    .from('clients')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

/**
 * Restore a soft-deleted client
 */
export async function restoreClient(id: string): Promise<void> {
  return restoreRecord('clients', id)
}
